import { CSSProperties } from 'react';

import { ApiFilestackImageData } from 'api/types/ApiFilestackImageData';
import { Image } from 'components/Image/Image';
import { FilestackImageFit, FilestackImageQuality } from 'types/Filestack';

import { getFilestackImageAspectRatio } from './getFilestackImageAspectRatio';
import { getFilestackImageFormat } from './getFilestackImageFormat';
import { getFilestackImageProps } from './getFilestackImageProps';

type Props = {
  data: ApiFilestackImageData | undefined;
  alt?: string;
  height?: number;
  width?: number;
  expandSize?: boolean;
  quality?: FilestackImageQuality;
  objectFit?: 'cover' | 'contain';
  objectPosition?: CSSProperties['objectPosition'];
  aspectRatio?: { width: number; height: number };
  round?: boolean;
  fit?: FilestackImageFit;
  eagerLoad?: boolean;
  insecureSVG?: boolean; // allow arbitrary SVGs
  'data-qa-id'?: string;
};

/**
 * Note: images do not stretch beyond their original size
 */
export function FilestackImage({
  data,
  alt,
  width,
  height,
  expandSize,
  quality: qualityName,
  objectFit,
  objectPosition,
  aspectRatio: aspectRatioProp,
  round,
  fit,
  eagerLoad,
  insecureSVG,
  'data-qa-id': qaId,
}: Props) {
  const size = { width, height };
  const originalSize =
    data?.width && data?.height
      ? { width: data.width, height: data.height }
      : undefined;

  const handle = data?.handle;
  const mimetype = data?.mimetype;

  const quality = qualityName === 'high' ? 100 : 70;
  const format = getFilestackImageFormat(mimetype);

  const srcData = getFilestackImageProps({
    handle,
    mimetype,
    quality,
    format,
    fit,
    originalSize:
      data?.width && data?.height
        ? { width: data.width, height: data.height }
        : undefined,
    size,
    insecureSVG,
  });

  const aspectRatio =
    aspectRatioProp || getFilestackImageAspectRatio({ originalSize, size });

  return (
    <Image
      loadingSrc={srcData?.loadingSrc}
      src={srcData?.src || ''}
      srcSet={srcData?.srcSet}
      width={srcData?.size.width}
      height={srcData?.size.height}
      aspectRatio={aspectRatio}
      expandSize={expandSize}
      objectFit={objectFit}
      objectPosition={objectPosition}
      round={round}
      eagerLoad={eagerLoad}
      data-qa-id={qaId}
      alt={alt || data?.altText || ''}
    />
  );
}
